import { ChangeEvent, FC, useRef } from 'react';
import { toast } from 'sonner';

import { DocumentFill, TrashIcon, Upload } from '@/shared/assets';
import { openModal } from '@/shared/lib';
import { shortText } from '@/shared/lib/utils/text';
import { Typography } from '@/shared/ui';

import { DocumentModal } from './DocumentModal';

interface IUploadItemData {
  id: string;
  customFileName: string;
  file: File;
}

const UploadItem = ({
  item,
  onDelete,
}: {
  item: IUploadItemData;
  onDelete: (id: string) => void;
}) => {
  return (
    <div className="flex w-full items-center rounded-[20px] bg-base-300 px-5 py-1.5">
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center">
          <DocumentFill className="mr-4 h-6 w-6" />

          <div className="flex flex-col gap-1 py-4">
            <Typography
              variant="14-r"
              className="overflow-hidden text-ellipsis whitespace-nowrap"
            >
              {shortText(item.customFileName)}
            </Typography>
          </div>
        </div>
        <button
          onClick={() => {
            onDelete(item.id);
          }}
        >
          <TrashIcon className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
};

interface IUploadInputProps {
  files: IUploadItemData[];
  onAddFile: (file: File, customFileName: string) => void;
  onDeleteFile: (id: string) => void;
}

export const UploadInput: FC<IUploadInputProps> = ({
  files,
  onAddFile,
  onDeleteFile,
}) => {
  const maxFileSizeBytes = 10 * 1024 * 1024;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const openFilePicker = () => {
    const input = inputRef.current;
    if (!input) return;
    if (typeof input.showPicker === 'function') {
      input.showPicker();
      return;
    }
    input.click();
  };

  const handleUpload = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (!e.target.files?.length) return;
    const selectedFile = e.target.files[0];
    if (selectedFile.size > maxFileSizeBytes) {
      toast.error('File too large. Maximum file size is 10 MB');
      e.target.value = '';
      return;
    }
    openModal({
      id: 'doc-name-modal',
      component: () => (
        <DocumentModal
          doc={selectedFile}
          existingNames={files.map((item) => item.customFileName)}
          onSave={(customFileName) => {
            onAddFile(selectedFile, customFileName);
          }}
        />
      ),
    });
    e.target.value = '';
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex w-full items-center rounded-[20px] bg-base-300 px-5">
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center">
            <DocumentFill className="mr-4 h-6 w-6" />

            <div className="flex flex-col py-4">
              <Typography
                variant="14-r"
                className="overflow-hidden text-ellipsis whitespace-nowrap"
              >
                Documents
              </Typography>
              <Typography variant="14-r" color="text-base-100">
                (Optional)
              </Typography>
            </div>
          </div>
          <input
            type="file"
            onChange={handleUpload}
            //multiple
            className="sr-only"
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
            ref={inputRef}
          />
          <button
            type="button"
            className="flex min-h-10 cursor-pointer items-center rounded-xl bg-brand-300 px-[14px] py-3 text-sm font-medium text-brand-500 hover:bg-hover-brand-300-hover active:bg-active-brand-300-active"
            onClick={openFilePicker}
            onPointerDown={(event) => {
              event.preventDefault();
              openFilePicker();
            }}
          >
            <Upload className="h-5 w-5" />

            <span>Upload</span>
          </button>
        </div>
      </div>
      {files.map((item) => (
        <UploadItem key={item.id} item={item} onDelete={onDeleteFile} />
      ))}
    </div>
  );
};
